import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TicketModal from './TicketModal';

const BookingHistoryTable = ({ bookings = [] }) => {
  const [sortBy, setSortBy] = useState('event_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  // Sort bookings
  const sortedBookings = [...bookings]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'event_date':
        aValue = a?.events?.event_date ? new Date(a.events.event_date) : new Date(0);
        bValue = b?.events?.event_date ? new Date(b.events.event_date) : new Date(0);
        break;
      case 'title':
        aValue = a?.events?.title?.toLowerCase() || '';
        bValue = b?.events?.title?.toLowerCase() || '';
        break;
      case 'amount':
        aValue = a?.total_amount || 0;
        bValue = b?.total_amount || 0;
        break;
      default:
        aValue = a?.created_at ? new Date(a.created_at) : new Date(0);
        bValue = b?.created_at ? new Date(b.created_at) : new Date(0);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Filter bookings
  const filteredBookings = filterStatus === 'all' 
    ? sortedBookings 
    : sortedBookings?.filter(booking => booking?.status === filterStatus);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleViewTicket = (booking) => {
    setSelectedBooking(booking);
    setShowTicketModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'attended':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'no_show':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-muted-foreground hover:text-foreground"
    >
      <span>{children}</span>
      {sortBy === field && (
        <Icon 
          name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
          size={14} 
        />
      )}
    </button>
  );

  if (filteredBookings?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No booking history</h3>
        <p className="text-muted-foreground">
          {filterStatus === 'all' ?'You haven\'t attended any events yet.'
            : `No bookings with status "${filterStatus}".`}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="all">All Status</option>
            <option value="attended">Attended</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No Show</option>
          </select>
        </div>
        
        <div className="text-sm text-muted-foreground">
          {filteredBookings?.length} booking{filteredBookings?.length !== 1 ? 's' : ''}
        </div>
      </div>
      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  <SortButton field="title">Event</SortButton>
                </th>
                <th className="px-4 py-3 text-left">
                  <SortButton field="event_date">Date</SortButton>
                </th>
                <th className="px-4 py-3 text-left">Ticket</th>
                <th className="px-4 py-3 text-left">
                  <SortButton field="amount">Amount</SortButton>
                </th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredBookings?.map((booking) => (
                <tr key={booking?.id} className="hover:bg-muted/30">
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
                        {booking?.events?.image_url ? (
                          <img
                            src={booking?.events?.image_url}
                            alt={booking?.events?.title || 'Event'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Image" size={16} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {booking?.events?.title || 'Untitled Event'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {booking?.events?.venue_name || 'Venue TBD'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      {booking?.events?.event_date ? (
                        <>
                          <p className="text-foreground">
                            {new Date(booking.events.event_date)?.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-muted-foreground">
                            {new Date(booking.events.event_date)?.toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </p>
                        </>
                      ) : (
                        <span className="text-muted-foreground">TBD</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <p className="text-foreground">
                        {booking?.event_tickets?.name || 'General'}
                      </p>
                      <p className="text-muted-foreground">
                        Qty: {booking?.quantity || 1}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-semibold text-foreground">
                      ${booking?.total_amount?.toFixed(2) || '0.00'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking?.status)}`}>
                      {booking?.status?.charAt(0)?.toUpperCase() + booking?.status?.slice(1) || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/events/${booking?.event_id}`}>
                          <Icon name="Eye" size={14} />
                        </Link>
                      </Button>
                      
                      {booking?.receipt_url && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={booking?.receipt_url} target="_blank" rel="noopener noreferrer">
                            <Icon name="Download" size={14} />
                          </a>
                        </Button>
                      )}
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewTicket(booking)}
                        title="View Ticket"
                      >
                        <Icon name="QrCode" size={14} />
                      </Button>
                      
                      {booking?.status === 'attended' && booking?.can_review && (
                        <Button variant="ghost" size="sm" title="Leave Review">
                          <Icon name="Star" size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Ticket Modal */}
      <TicketModal 
        booking={selectedBooking} 
        isOpen={showTicketModal} 
        onClose={() => {
          setShowTicketModal(false);
          setSelectedBooking(null);
        }} 
      />
    </div>
  );
};

export default BookingHistoryTable;